import { Component, OnInit, SecurityContext, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ApiService } from '@core/services/api.service';
import { HeaderService } from '@core/services/header.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';

const CATEGORIES = ['general', 'onboarding', 'offboarding', 'leave', 'payroll', 'attendance', 'performance', 'announcement'];

@Component({
  standalone: false,
  selector: 'app-email-templates',
  templateUrl: './email-templates.component.html',
  styleUrls: ['./email-templates.component.scss']
})
export class EmailTemplatesComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  activePageTab: 'templates' | 'triggers' | 'logs' | 'smtp' = 'templates';

  // Catalog of automated emails the system sends, for the Triggers view. `system: true` means the
  // email is generated with self-contained branded HTML in code (not driven by an editable template);
  // otherwise HR can bind/toggle an editable template whose triggerEvent matches `key`.
  triggerCatalog: { group: string; icon: string; events: { key: string; label: string; desc: string; system?: boolean; scheduled?: boolean }[] }[] = [
    { group: 'Attendance', icon: 'schedule', events: [
      { key: 'ATTENDANCE_REMINDER', label: 'Shift check-in / check-out nudges', desc: '15 min before & 5 min after each employee’s shift start/end.', system: true, scheduled: true },
      { key: 'ATTENDANCE_ABSENT', label: 'Marked absent', desc: 'End-of-day notice when no attendance was recorded.', system: true, scheduled: true },
      { key: 'HOLIDAY_REMINDER', label: 'Upcoming holiday', desc: 'Sent the day before a company holiday.', system: true, scheduled: true },
      { key: 'attendance_regularise_applied', label: 'Regularisation applied', desc: 'To the approver when someone requests a regularisation.' },
      { key: 'attendance_regularise_approved', label: 'Regularisation approved', desc: 'To the employee when approved.' },
      { key: 'attendance_regularise_rejected', label: 'Regularisation rejected', desc: 'To the employee when rejected.' },
    ]},
    { group: 'Leave', icon: 'beach_access', events: [
      { key: 'leave_applied', label: 'Leave applied', desc: 'To the approver on a new leave request.' },
      { key: 'leave_approved', label: 'Leave approved', desc: 'To the employee when approved.' },
      { key: 'leave_rejected', label: 'Leave rejected', desc: 'To the employee when rejected.' },
    ]},
    { group: 'Onboarding & Hiring', icon: 'person_add', events: [
      { key: 'employee_created', label: 'Welcome / credentials', desc: 'New employee account created.' },
      { key: 'candidate_invite', label: 'Candidate portal invite', desc: 'Link to complete the candidate form.', system: true },
      { key: 'onboarding_task_assigned', label: 'Onboarding task assigned', desc: 'Task assigned to a new joiner.' },
    ]},
    { group: 'Payroll', icon: 'payments', events: [
      { key: 'PAYSLIP_READY', label: 'Payslip ready', desc: 'When a payslip is marked paid.', system: true },
    ]},
    { group: 'Performance', icon: 'insights', events: [
      { key: 'APPRAISAL_OPENED', label: 'Appraisal opened', desc: 'Self-assessment now open.', system: true },
      { key: 'APPRAISAL_COMPLETED', label: 'Appraisal completed', desc: 'Manager finalised the appraisal.', system: true },
      { key: 'FORM_ASSIGNED', label: 'Form awaiting you', desc: 'A form/pipeline stage needs the person’s input.', system: true },
      { key: 'GOAL_ASSIGNED', label: 'New goal set', desc: 'A performance goal was assigned.', system: true },
      { key: 'REVIEW_SHARED', label: 'Review shared', desc: 'A performance review was shared.', system: true },
    ]},
    { group: 'Company', icon: 'campaign', events: [
      { key: 'ANNOUNCEMENT_POSTED', label: 'New announcement', desc: 'Broadcast to all active staff.', system: true },
    ]},
  ];

  templateForEvent(key: string): any {
    return this.templates.find(t => (t.triggerEvent || '') === key) || null;
  }

  templates: any[] = [];
  loading = false;
  searchText = '';
  filterCategory = 'all';
  categories = CATEGORIES;
  tmplPage = 1;
  tmplPageSize = 9;

  drawerOpen = false;
  editingId: string | null = null;
  submitting = false;
  activeDrawerTab: 'editor' | 'preview' = 'editor';

  form: any = this.emptyForm();

  previewHtml: SafeHtml = '';
  previewParams: { [key: string]: string } = {};
  detectedParams: string[] = [];

  showPreviewModal = false;
  previewModalHtml: SafeHtml = '';

  showCardPreview = false;
  cardPreviewTemplate: any = null;
  cardPreviewHtml: SafeHtml = '';

  showTestSend = false;
  testSendTemplateId = '';
  testSendEmail = '';
  testSendParams: { [key: string]: string } = {};
  testSendDetectedParams: string[] = [];
  testSending = false;

  logs: any[] = [];
  logsLoading = false;
  logStats: any = { total: 0, sent: 0, failed: 0, skipped: 0 };
  logFilter = 'all';

  smtpForm: any = { smtpEnabled: false, smtpHost: '', smtpPort: 587, smtpUsername: '', smtpPassword: '', smtpFromEmail: '', smtpFromName: '' };
  smtpSubmitting = false;
  smtpLoaded = false;

  constructor(
    private apiService: ApiService,
    private headerService: HeaderService,
    private snackbar: SnackbarService,
    private confirmDialog: ConfirmDialogService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.headerService.setTitle('Email Templates');
    this.load();
  }

  switchPageTab(tab: 'templates' | 'triggers' | 'logs' | 'smtp'): void {
    this.activePageTab = tab;
    if (tab === 'logs' && this.logs.length === 0) this.loadLogs();
    if (tab === 'smtp' && !this.smtpLoaded) this.loadSmtp();
  }

  // ── Logs analytics ──────────────────────────────────────────────────────────
  get logSentPct(): number {
    const t = this.logStats?.total || 0;
    return t ? Math.round(((this.logStats.sent || 0) / t) * 100) : 0;
  }
  get logFailedPct(): number {
    const t = this.logStats?.total || 0;
    return t ? Math.round(((this.logStats.failed || 0) / t) * 100) : 0;
  }
  // Top events by volume in the currently-loaded logs, for the breakdown strip.
  get logEventBreakdown(): { label: string; count: number; pct: number }[] {
    const counts: Record<string, number> = {};
    for (const l of this.logs) {
      const key = l.triggerEvent || l.templateName || 'Other';
      counts[key] = (counts[key] || 0) + 1;
    }
    const total = this.logs.length || 1;
    return Object.entries(counts)
      .map(([k, count]) => ({ label: this.getTriggerLabel(k), count, pct: Math.round((count / total) * 100) }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }

  // Insert a {{variable}} at the end of the body from the Triggers/editor chips.
  insertVariable(name: string): void {
    this.form.htmlBody = (this.form.htmlBody || '') + `{{${name}}}`;
    this.onHtmlChange();
  }
  readonly commonVariables = ['firstName', 'lastName', 'employeeId', 'companyName', 'year', 'ctaLink', 'ctaLabel', 'bodyContent'];


  emptyForm(): any {
    return {
      name: '', subject: '', htmlBody: this.defaultHtml(),
      category: 'general', description: '', triggerEvent: '', active: true
    };
  }

  load(): void {
    this.loading = true;
    this.apiService.get('/email-templates').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => { this.templates = Array.isArray(data) ? data : (data?.data || []); this.loading = false; },
      error: () => { this.loading = false; }
    });
  }

  get filtered(): any[] {
    return this.templates.filter(t => {
      const q = this.searchText.toLowerCase();
      const matchSearch = !q || `${t.name} ${t.subject} ${t.category} ${t.description}`.toLowerCase().includes(q);
      const matchCat = this.filterCategory === 'all' || t.category === this.filterCategory;
      return matchSearch && matchCat;
    });
  }

  get pagedTemplates(): any[] {
    const start = (this.tmplPage - 1) * this.tmplPageSize;
    return this.filtered.slice(start, start + this.tmplPageSize);
  }

  get tmplTotalPages(): number {
    return Math.ceil(this.filtered.length / this.tmplPageSize) || 1;
  }

  get tmplPageNumbers(): number[] {
    return Array.from({ length: this.tmplTotalPages }, (_, i) => i + 1);
  }

  onSearchChange(): void {
    this.tmplPage = 1;
  }

  onCategoryChange(): void {
    this.tmplPage = 1;
  }

  openCardPreview(t: any): void {
    this.cardPreviewTemplate = t;
    let html = t.htmlBody || '<p style="padding:20px;color:#666">No HTML body defined.</p>';
    const regex = /\{\{([a-zA-Z0-9_]+)\}\}/g;
    html = html.replace(regex, (_: string, p: string) =>
      `<span style="background:#fef3c7;color:#92400e;padding:0 3px;border-radius:3px;font-family:monospace;font-size:0.85em">{{${p}}}</span>`
    );
    this.cardPreviewHtml = this.sanitizer.bypassSecurityTrustHtml(html);
    this.showCardPreview = true;
  }

  openCreate(): void {
    this.editingId = null;
    this.form = this.emptyForm();
    this.activeDrawerTab = 'editor';
    this.previewParams = {};
    this.detectedParams = [];
    this.detectParams();
    this.renderPreview();
    this.drawerOpen = true;
  }

  openEdit(t: any): void {
    this.editingId = t.id;
    this.form = { name: t.name, subject: t.subject, htmlBody: t.htmlBody, category: t.category || 'general', description: t.description || '', triggerEvent: t.triggerEvent || '', active: t.active };
    this.activeDrawerTab = 'editor';
    this.detectParams();
    this.renderPreview();
    this.drawerOpen = true;
  }

  closeDrawer(): void {
    this.drawerOpen = false;
    this.showPreviewModal = false;
  }

  onHtmlChange(): void {
    this.detectParams();
    this.renderPreview();
  }

  detectParams(): void {
    const matches = new Set<string>();
    const regex = /\{\{([a-zA-Z0-9_]+)\}\}/g;
    let m;
    const combined = (this.form.subject || '') + (this.form.htmlBody || '');
    while ((m = regex.exec(combined)) !== null) matches.add(m[1]);
    this.detectedParams = Array.from(matches);
    this.detectedParams.forEach(p => {
      if (!(p in this.previewParams)) this.previewParams[p] = '';
    });
  }

  renderPreview(): void {
    let html = this.form.htmlBody || '';
    this.detectedParams.forEach(p => {
      const val = this.previewParams[p];
      if (val) {
        html = html.split(`{{${p}}}`).join(val);
      }
    });
    this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(html);
  }

  onFrameLoad(iframe: HTMLIFrameElement): void {
    try {
      const h = iframe.contentDocument?.documentElement?.scrollHeight
             || iframe.contentDocument?.body?.scrollHeight
             || 0;
      if (h > 200) iframe.style.height = h + 'px';
    } catch {}
  }

  switchDrawerTab(tab: 'editor' | 'preview'): void {
    this.activeDrawerTab = tab;
    if (tab === 'preview') { this.detectParams(); this.renderPreview(); }
  }

  openFullPreview(): void {
    this.detectParams();
    this.renderPreview();
    this.previewModalHtml = this.previewHtml;
    this.showPreviewModal = true;
  }

  submit(): void {
    if (!this.form.name?.trim()) { this.snackbar.showFailure('Template name is required'); return; }
    if (!this.form.subject?.trim()) { this.snackbar.showFailure('Subject is required'); return; }
    if (!this.form.htmlBody?.trim()) { this.snackbar.showFailure('HTML body is required'); return; }
    this.submitting = true;
    const req = this.editingId
      ? this.apiService.put('/email-templates/' + this.editingId, this.form)
      : this.apiService.post('/email-templates', this.form);
    req.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snackbar.showSuccess(this.editingId ? 'Template updated' : 'Template created');
        this.submitting = false;
        this.drawerOpen = false;
        this.load();
      },
      error: () => { this.snackbar.showFailure('Failed to save template'); this.submitting = false; }
    });
  }

  delete(id: string, name: string): void {
    this.confirmDialog.confirm(`"${name}" will be permanently deleted.`, {
      title: 'Delete Template', confirmLabel: 'Delete', danger: true
    }).then(ok => {
      if (!ok) return;
      this.apiService.delete('/email-templates/' + id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => { this.snackbar.showSuccess('Template deleted'); this.load(); },
        error: () => this.snackbar.showFailure('Failed to delete')
      });
    });
  }

  toggleActive(t: any): void {
    this.apiService.put('/email-templates/' + t.id, { ...t, active: !t.active }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { t.active = !t.active; },
      error: () => this.snackbar.showFailure('Failed to update')
    });
  }

  openTestSend(t: any): void {
    this.testSendTemplateId = t.id;
    this.testSendEmail = '';
    const regex = /\{\{([a-zA-Z0-9_]+)\}\}/g;
    const combined = (t.subject || '') + (t.htmlBody || '');
    const matches = new Set<string>();
    let m;
    while ((m = regex.exec(combined)) !== null) matches.add(m[1]);
    this.testSendDetectedParams = Array.from(matches);
    this.testSendParams = {};
    this.testSendDetectedParams.forEach(p => this.testSendParams[p] = '');
    this.showTestSend = true;
  }

  submitTestSend(): void {
    if (!this.testSendEmail.trim()) { this.snackbar.showFailure('Recipient email is required'); return; }
    this.testSending = true;
    this.apiService.post(`/email-templates/${this.testSendTemplateId}/test-send`, {
      toEmail: this.testSendEmail,
      params: this.testSendParams
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.snackbar.showSuccess('Test email sent!'); this.showTestSend = false; this.testSending = false; },
      error: () => { this.snackbar.showFailure('Failed to send test email'); this.testSending = false; }
    });
  }

  getParams(t: any): string[] {
    if (!t.parameters) return [];
    return t.parameters.split(',').map((p: string) => p.trim()).filter(Boolean);
  }

  getTriggerLabel(triggerEvent: string): string {
    const map: Record<string, string> = {
      employee_created:                 'Employee Created',
      employee_offboarded:              'Employee Offboarded',
      leave_applied:                    'Leave Applied',
      leave_approved:                   'Leave Approved',
      leave_rejected:                   'Leave Rejected',
      attendance_regularise_applied:    'Regularisation Applied',
      attendance_regularise_approved:   'Regularisation Approved',
      attendance_regularise_rejected:   'Regularisation Rejected',
      payslip_generated:                'Payslip Generated',
      password_reset:                   'Password Reset',
      password_reset_by_admin:          'Password Reset by Admin',
      checkin_reminder:                 'Check-In Reminder',
      checkout_reminder:                'Check-Out Reminder',
      performance_review_due:           'Performance Review Due',
    };
    return map[triggerEvent] || triggerEvent.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  getCategoryColor(cat: string): string {
    const map: any = {
      onboarding: '#166534', offboarding: '#7c2d12', leave: '#1e40af',
      payroll: '#6b21a8', attendance: '#c97d10', performance: '#0e7490',
      announcement: '#334155', general: '#374151'
    };
    return map[cat] || '#374151';
  }

  insertSnippet(snippet: string): void {
    this.form.htmlBody += snippet;
    this.detectParams();
  }


  loadLogs(): void {
    this.logsLoading = true;
    this.apiService.get('/email-logs').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => { this.logs = Array.isArray(data) ? data : []; this.logsLoading = false; },
      error: () => { this.logsLoading = false; }
    });
    this.apiService.get('/email-logs/stats').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (s: any) => { this.logStats = s; },
      error: () => {}
    });
  }

  get filteredLogs(): any[] {
    if (this.logFilter === 'all') return this.logs;
    return this.logs.filter(l => l.status === this.logFilter.toUpperCase());
  }

  recallingLogId: string | null = null;
  recallConfirmId: string | null = null;
  resendingLogId: string | null = null;

  resendLog(log: any): void {
    if (this.resendingLogId) return;
    this.resendingLogId = log.id;
    this.apiService.post('/email-logs/' + log.id + '/resend', {}).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.resendingLogId = null;
        this.snackbar.showSuccess('Email resent to ' + log.recipientEmail);
        this.loadLogs();
      },
      error: (err: any) => {
        this.resendingLogId = null;
        this.snackbar.showFailure(err?.error?.message || 'Could not resend the email');
      }
    });
  }

  confirmRecall(log: any): void {
    this.recallConfirmId = log.id;
  }

  cancelRecall(): void {
    this.recallConfirmId = null;
  }

  recallLog(log: any): void {
    this.recallConfirmId = null;
    this.recallingLogId = log.id;
    this.apiService.post('/email-logs/' + log.id + '/recall', {}).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.recallingLogId = null;
        this.snackbar.showSuccess('Retraction email sent to ' + log.recipientEmail);
        this.loadLogs();
      },
      error: (err: any) => {
        this.recallingLogId = null;
        this.snackbar.showFailure(err?.error?.message || 'Failed to recall email');
      }
    });
  }

  deleteLog(id: string): void {
    this.apiService.delete('/email-logs/' + id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.logs = this.logs.filter(l => l.id !== id); this.logStats.total = Math.max(0, this.logStats.total - 1); },
      error: () => this.snackbar.showFailure('Failed to delete log')
    });
  }

  getStatusColor(status: string): string {
    return status === 'SENT' ? '#166534' : status === 'FAILED' ? '#991b1b' : '#374151';
  }
  getStatusBg(status: string): string {
    return status === 'SENT' ? '#dcfce7' : status === 'FAILED' ? '#fef2f2' : '#f3f4f6';
  }


  loadSmtp(): void {
    this.apiService.get('/company-settings').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (s: any) => {
        this.smtpForm = {
          smtpEnabled: s.smtpEnabled || false,
          smtpHost: s.smtpHost || '',
          smtpPort: s.smtpPort || 587,
          smtpUsername: s.smtpUsername || '',
          smtpPassword: '',
          smtpFromEmail: s.smtpFromEmail || '',
          smtpFromName: s.smtpFromName || ''
        };
        this.smtpLoaded = true;
      },
      error: () => { this.smtpLoaded = true; }
    });
  }

  saveSmtp(): void {
    this.smtpSubmitting = true;
    this.apiService.patch('/company-settings/smtp', this.smtpForm).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.snackbar.showSuccess('SMTP settings saved'); this.smtpSubmitting = false; },
      error: () => { this.snackbar.showFailure('Failed to save SMTP settings'); this.smtpSubmitting = false; }
    });
  }

  private defaultHtml(): string {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { font-family: Arial, sans-serif; background: #f4f4f4; margin: 0; padding: 20px; }
    .container { max-width: 600px; margin: 0 auto; background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
    .header { background: #334155; color: #fff; padding: 28px 32px; }
    .header h1 { margin: 0; font-size: 22px; }
    .body { padding: 28px 32px; color: #131b2e; line-height: 1.7; }
    .footer { padding: 16px 32px; background: #f9f9f9; border-top: 1px solid #eee; font-size: 12px; color: #888; text-align: center; }
    .btn { display: inline-block; background: #334155; color: #fff !important; padding: 12px 28px; border-radius: 6px; text-decoration: none; font-weight: 600; margin-top: 16px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>{{subject}}</h1>
    </div>
    <div class="body">
      <p>Dear {{firstName}},</p>
      <p>{{bodyContent}}</p>
      <a href="{{ctaLink}}" class="btn">{{ctaLabel}}</a>
    </div>
    <div class="footer">
      &copy; {{companyName}} · {{year}} · This is an automated email.
    </div>
  </div>
</body>
</html>`;
  }
}
